"use client";

import { buildMenu, moveWithinGroup, isPinnedMyBb, isMenuSuperAdmin, EPX_PREF_ID, type MenuItem } from '@/lib/menu-config';
import { MENU_ICONS } from '@/components/layout/menu-icons';
import { Fragment, useState, useMemo, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
  sidebarMenuButtonVariants,
} from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LogIn, LogOut, Loader2, Palette, GripVertical, Download, UserPlus, Plus, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePWAInstall } from '@/hooks/use-pwa-install';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { useVisibility } from '@/contexts/visibility-context';
import { useUserLayoutPrefs } from '@/contexts/user-layout-prefs-context';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { HelpBubble } from '@/components/help/help-bubble';
import { useHelp } from '@/contexts/help-context';
import { useSavedAccounts } from '@/hooks/use-saved-accounts';
import { useRouter } from 'next/navigation';

/** Inline theme-picker sidebar button so it matches SidebarMenuButton style.
 *  The ThemePicker itself is rendered at the app-shell root level (above the
 *  mobile sidebar Sheet) so it is never clipped when the Sheet closes.
 *  We fire a custom window event so the root-level picker opens from there. */
function ThemeSidebarItem({ buttonSize, label }: { buttonSize: 'icon' | 'mobile' | 'grid' | 'default' | 'mobile-grid'; label: string }) {
  const { isMobile, setOpen: setSidebarOpen } = useSidebar();

  const handleOpen = () => {
    if (isMobile) {
      // Close the sidebar sheet first so it slides away cleanly
      setSidebarOpen(false);
    }
    // Small delay lets the sheet close animation start before the picker slides in
    setTimeout(() => window.dispatchEvent(new Event('bb-open-theme-picker')), isMobile ? 150 : 0);
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        size={buttonSize}
        isActive={false}
        onClick={handleOpen}
        tooltip={{ children: <span className="text-xs">{label}</span> }}
      >
        <Palette />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** Help tooltip content for each nav route — used when help mode is active. */
const NAV_HELP: Record<string, { desc: string; anchor: string }> = {
  '/home':            { desc: 'The main feed — browse music, artists, events, news, and more. Use the filter bar to jump to any section.', anchor: 'home-feed' },
  '/dashboard':       { desc: 'Your personal dashboard with stats, recent activity, and quick actions.', anchor: 'getting-started' },
  '/profile':         { desc: 'Your public profile — manage your Wall, music, marketplace items, and social links.', anchor: 'social' },
  '/marketplace':     { desc: 'Buy and sell beats, samples, merch, and services. Creators can list items from here.', anchor: 'marketplace' },
  '/jam-session':     { desc: 'Real-time virtual jam rooms — collaborate with other musicians live.', anchor: 'creators' },
  '/party':           { desc: 'Start or join a Listening Party — everyone hears the same track in perfect sync.', anchor: 'events' },
  '/feed':            { desc: 'Activity feed showing posts and updates from people you follow.', anchor: 'social' },
  '/events':          { desc: 'Upcoming gigs, showcases, and online events. Filter by location or genre.', anchor: 'events' },
  '/news':            { desc: 'Music industry news, artist announcements, and label updates.', anchor: 'getting-started' },
  '/charts':          { desc: 'Top charts — most-played tracks across the platform, updated in real time.', anchor: 'music' },
  '/radio':           { desc: 'Genre-curated radio streams. All stations play through the built-in media player.', anchor: 'radio' },
  '/radio/podcasts':  { desc: 'Long-form podcast episodes from creators in the community.', anchor: 'radio' },
  '/radio/mixtapes':  { desc: 'BB Mixtapes — the full cultural history of the mixtape, from home tapes to the streaming era.', anchor: 'radio' },
  '/radio/record':    { desc: 'Record your own radio show with DJ commentary. BB catalog music only — already rights-cleared.', anchor: 'radio' },
  '/platform':        { desc: 'Platform transparency — live costs, revenue, and platform health report.', anchor: 'getting-started' },
  '/wishlist':        { desc: 'Your personal wish list — save items you want to come back to later.', anchor: 'getting-started' },
  '/synchronization': { desc: 'Find sync licensing opportunities — pitch your music to TV, film, and ad placements.', anchor: 'creators' },
  '/stats':           { desc: 'Analytics for your content — track plays, downloads, and audience growth.', anchor: 'creators' },
  '/academy':         { desc: 'BB Academy — courses on money, music business, health, and the exponential function. Plus AI tutor and curated resources.', anchor: 'creators' },
  '/media':           { desc: 'Media Vault — upload, organise, and manage all your tracks and releases.', anchor: 'creators' },
  '/artwork-studio':  { desc: 'Generate and edit release artwork using AI.', anchor: 'creators' },
  '/film-maker':      { desc: 'Create music videos and visual content directly in the browser.', anchor: 'creators' },
  '/wallet':          { desc: "Your Breakin' Bread wallet — manage credits, payouts, and transaction history.", anchor: 'getting-started' },
  '/earnings':        { desc: 'Creator earnings dashboard — track revenue from streams, downloads, and sales.', anchor: 'creators' },
  '/publishing':      { desc: 'Publishing rights — STIM works, royalty breakdowns per copyright, unclaimed accounts, and identity verification.', anchor: 'creators' },
  '/bookkeeping':     { desc: 'Record income and expenses for your music business. Export reports for accounting.', anchor: 'creators' },
  '/dj':              { desc: 'DJ Mixer — mix tracks using the built-in browser-based mixing console.', anchor: 'creators' },
  '/creator-ai':      { desc: 'BB AI Advisor — get expert guidance on contracts, marketing, publishing, and finance.', anchor: 'chat' },
  '/legal-ai':        { desc: 'BB Legal Department — proactive rights monitoring, contract analysis, royalty tracking, and AI legal guidance.', anchor: 'creators' },
  '/studio/recorder': { desc: 'In-browser audio recorder — capture ideas, vocals, and samples instantly.', anchor: 'creators' },
  '/studio/songwriter':{ desc: 'AI-assisted songwriting studio — generate lyrics, chord progressions, and song structures.', anchor: 'creators' },
  '/settings':        { desc: 'App settings — notifications, privacy, connected accounts, and more.', anchor: 'getting-started' },
  '/admin':           { desc: 'Super Admin panel — manage users, content, themes, and platform settings.', anchor: 'getting-started' },
};

/**
 * The menu itself is defined in lib/menu-config (one list for the sidebar,
 * the phone tray, Settings → Layout, coπ and the auth gate). Re-exported here
 * for older importers.
 */
export { NAV_PAGES, PUBLIC_NAV_HREFS } from '@/lib/menu-config';

/** Shows a "Download App" menu item when the PWA is installable. Hidden once
 *  installed or never-dismissed. Members find it in the account menu,
 *  visitors just above the bottom row (Andrés's menu, 2026-11-03). */
function PWAInstallNavItem({ buttonSize }: { buttonSize: 'icon' | 'mobile' | 'grid' | 'default' | 'mobile-grid' }) {
  const t = useTranslations('nav');
  const { canInstall, isIos, isInstalled, neverDismissed, install } = usePWAInstall();
  const [showIosHint, setShowIosHint] = useState(false);
  const [installing, setInstalling] = useState(false);

  if (isInstalled || neverDismissed) return null;
  if (!canInstall && !isIos) return null;

  const handleClick = async () => {
    if (isIos) { setShowIosHint(h => !h); return; }
    setInstalling(true);
    await install();
    setInstalling(false);
  };

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton
          size={buttonSize}
          isActive={false}
          onClick={handleClick}
          tooltip={{ children: <span className="text-xs">{t('downloadApp')}</span> }}
          className="text-primary"
        >
          <Download />
          <span>{installing ? t('installing') : t('downloadApp')}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {showIosHint && isIos && (
        <li className="col-span-full mx-2 mb-1 list-none rounded-xl border border-border/50 bg-muted/40 p-3 text-2xs leading-relaxed text-muted-foreground">
          {t('iosInstallHint')}
        </li>
      )}
    </>
  );
}

type ButtonSize = 'icon' | 'mobile' | 'grid' | 'default' | 'mobile-grid';

/** A small group title ("Explore", "My BB", "More") — list and grid views;
 *  icon-only view gets a thin line instead. */
function GroupHeading({ label, iconOnly, grid, first }: { label: string; iconOnly: boolean; grid: boolean; first?: boolean }) {
  if (iconOnly) {
    return first ? null : <li aria-hidden="true" className="mx-3 my-1 h-px list-none bg-sidebar-border/70" />;
  }
  return (
    <li className={cn('list-none [direction:ltr]', grid && 'col-span-full')}>
      <h2 className={cn(
        'text-left text-2xs font-semibold uppercase tracking-wider text-sidebar-foreground menuright:text-right',
        grid ? 'px-2 pb-1 pt-2' : 'px-4 pb-0.5 pt-3',
        first && 'pt-1',
      )}>
        {label}
      </h2>
    </li>
  );
}

/** "+ Create" — opens its shortcuts in place. A plain button (not
 *  SidebarMenuButton, which closes the phone sheet / folds the desktop menu
 *  on every click). */
function CreateToggle({ open, onToggle, size, label, controls }: { open: boolean; onToggle: () => void; size: ButtonSize; label: string; controls: string }) {
  const btn = (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className={cn(sidebarMenuButtonVariants({ size }), open && 'bg-sidebar-accent text-sidebar-accent-foreground')}
    >
      <Plus className={cn('transition-transform duration-200 motion-reduce:transition-none', open && 'rotate-45')} aria-hidden />
      <span className={cn(
        size === 'icon' && 'sr-only',
        size === 'grid' || size === 'mobile-grid' ? 'mt-0.5 w-full text-center leading-tight line-clamp-2' : 'truncate',
      )}>{label}</span>
    </button>
  );
  if (size !== 'icon') return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="right" align="center" sideOffset={10} className="bg-sidebar text-sidebar-foreground border-sidebar-border">
        <span className="text-xs">{label}</span>
      </TooltipContent>
    </Tooltip>
  );
}

const CREATE_OPEN_KEY = 'bb-menu-create-open';

export function SidebarNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { isMobile, displayMode, menuView, setOpenMobile } = useSidebar();
  const { user, userProfile, loading, signOut } = useAuth();
  const t = useTranslations('nav');
  const tAuth = useTranslations('auth');
  const isIconOnly = !isMobile && displayMode === 'icon';

  // ── Saved accounts for quick-switch ──────────────────────────────────────
  const { accounts: savedAccounts } = useSavedAccounts();
  // Only show accounts that are NOT the current user
  const otherSavedAccounts = savedAccounts.filter(a => a.uid !== user?.uid);

  // Switching (or adding) an account = sign out, then the sign-in page, which
  // lists the saved accounts. A plain link to /signin bounced a signed-in
  // person straight back to Home, so "Add account" never worked.
  const handleAccountSwitch = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMobile) setOpenMobile(false);
    await signOut();
    router.push('/signin');
  };

  const isGridView = (!isIconOnly && menuView === 'grid') || (isMobile && menuView === 'grid');
  let buttonSize: ButtonSize = 'default';
  if (isIconOnly) {
    buttonSize = 'icon';
  } else if (isMobile) {
    buttonSize = isGridView ? 'mobile-grid' : 'mobile';
  } else {
    buttonSize = isGridView ? 'grid' : 'default';
  }

  const { isPageVisible } = useVisibility();
  const { navPrefs, updateNavPrefs } = useUserLayoutPrefs();
  const superAdmin = isMenuSuperAdmin(user?.email, userProfile?.roles, userProfile?.accountType);
  const { helpEnabled } = useHelp();

  // Explore (everyone) · My BB (+ Create first) · More (members) · bottom row.
  const menu = useMemo(
    () => buildMenu({ signedIn: !!user, navPrefs, isPageVisible, superAdmin }),
    [user, navPrefs, isPageVisible, superAdmin],
  );
  const itemName = useCallback((item: MenuItem) => (item.navKey ? t(item.navKey as any) : item.label), [t]);

  // "+ Create" open or shut — remembered on this device.
  const [createOpen, setCreateOpen] = useState(false);
  useEffect(() => {
    try { setCreateOpen(localStorage.getItem(CREATE_OPEN_KEY) === '1'); } catch { /* private mode */ }
  }, []);
  const toggleCreate = useCallback(() => {
    setCreateOpen(open => {
      try { localStorage.setItem(CREATE_OPEN_KEY, open ? '0' : '1'); } catch { /* private mode */ }
      return !open;
    });
  }, []);

  // ── Account menu (bottom row) — opens in place above the avatar ─────────
  const [accountOpen, setAccountOpen] = useState(false);
  const accountButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => { setAccountOpen(false); }, [pathname, user?.uid]);
  const closeAccount = useCallback((refocus: boolean) => {
    setAccountOpen(false);
    if (refocus) accountButtonRef.current?.focus();
  }, []);
  const accountName = (userProfile?.displayName || user?.displayName || user?.email || '').trim();
  const accountPhoto = userProfile?.profileImageUrl || user?.photoURL || '';

  // ── Pointer-based drag-and-drop (works on mobile + desktop) — My BB only ─
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragStartY = useRef<number>(0);
  const isDragActive = useRef(false);
  const navContainerRef = useRef<HTMLUListElement | null>(null);

  // Dashboard + Profile are pinned first; only the rest can be dragged.
  const pinnedMyBb = useMemo(() => menu.mybb.filter(isPinnedMyBb), [menu.mybb]);
  const movableMyBb = useMemo(() => menu.mybb.filter(i => !isPinnedMyBb(i)), [menu.mybb]);
  const performReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    updateNavPrefs(moveWithinGroup(navPrefs, movableMyBb.map(i => i.id), fromIndex, toIndex));
  }, [movableMyBb, navPrefs, updateNavPrefs]);

  const getIndexAtY = useCallback((clientY: number): number | null => {
    if (!navContainerRef.current) return null;
    const items = navContainerRef.current.querySelectorAll<HTMLElement>('[data-nav-index]');
    for (const el of items) {
      const rect = el.getBoundingClientRect();
      if (clientY >= rect.top && clientY < rect.bottom) {
        return parseInt(el.getAttribute('data-nav-index') || '-1');
      }
    }
    return null;
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLElement>, index: number) => {
    if (!user) return;
    // Use closest() so clicks on SVG icons / spans *inside* <a> or <button> are caught too.
    // Without this, tagName checks miss SVG children, setPointerCapture fires on <li>, and
    // the resulting mouseup on <li> means the Next.js <Link> never receives the click.
    if ((e.target as HTMLElement).closest?.('a, button')) return;
    dragStartY.current = e.clientY;
    isDragActive.current = false;
    setDraggingIndex(index);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [user]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>, index: number) => {
    if (draggingIndex === null || draggingIndex !== index) return;
    // Activate drag only after moving 6px to avoid blocking normal clicks
    if (!isDragActive.current && Math.abs(e.clientY - dragStartY.current) > 6) {
      isDragActive.current = true;
    }
    if (!isDragActive.current) return;
    e.preventDefault();
    // Release capture so elementFromPoint works across elements
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    const over = getIndexAtY(e.clientY);
    if (over !== null) setDragOverIndex(over);
  }, [draggingIndex, getIndexAtY]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLElement>, index: number) => {
    if (draggingIndex === null || draggingIndex !== index) return;
    if (isDragActive.current && dragOverIndex !== null) {
      performReorder(draggingIndex, dragOverIndex);
    }
    setDraggingIndex(null);
    setDragOverIndex(null);
    isDragActive.current = false;
  }, [draggingIndex, dragOverIndex, performReorder]);

  const isItemActive = (item: MenuItem) => {
    if (!item.href) return false;
    if (item.id === EPX_PREF_ID) return pathname === '/studio/epx' || pathname === '/epx';
    if (item.id === '/admin/hermes') return !!pathname?.startsWith('/admin/hermes');
    return pathname === item.href.split('?')[0];
  };

  /** One page link (Explore, My BB, More, bottom) — help bubble in help mode. */
  const pageButton = (item: MenuItem, opts: { indent?: boolean } = {}) => {
    const Icon = MENU_ICONS[item.icon];
    const label = itemName(item);
    const button = (
      <SidebarMenuButton
        {...(item.href ? { href: item.href } : {})}
        onClick={item.event ? () => window.dispatchEvent(new CustomEvent(item.event!.name, { detail: item.event!.detail })) : undefined}
        size={buttonSize}
        isActive={isItemActive(item)}
        tooltip={helpEnabled && item.href ? undefined : { children: <span className="text-xs">{label}</span> }}
        className={opts.indent ? 'pl-5 menuright:pl-4 menuright:pr-5' : undefined}
        draggable={false}
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    );
    if (!helpEnabled || !item.href) return button;
    return (
      <HelpBubble
        title={label}
        description={NAV_HELP[item.href]?.desc ?? `Navigate to ${label}`}
        learnMoreAnchor={NAV_HELP[item.href]?.anchor}
        aiQuestion={`What is the ${label} section in Breakin' Bread?`}
        side="right"
        block
      >
        {button}
      </HelpBubble>
    );
  };

  const canDrag = !!user && !isIconOnly && !isGridView;
  const iconTip = (label: string) => ({ children: label, className: "bg-sidebar text-sidebar-foreground border-sidebar-border" });
  const withTooltip = (node: ReactElement, label: string) => (!isIconOnly ? node : (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="right" align="center" sideOffset={10} className="bg-sidebar text-sidebar-foreground border-sidebar-border">
        <span className="text-xs">{label}</span>
      </TooltipContent>
    </Tooltip>
  ));

  /** Switch account: the other saved accounts + Add account. Icon-only view
   *  stacks them as icons with tooltips; the others keep one wrapping row. */
  const switchAccountRows = () => {
    if (isIconOnly) {
      return (
        <>
          {otherSavedAccounts.map(account => {
            const name = account.displayName || account.email || '';
            return (
              <SidebarMenuItem key={account.uid}>
                {withTooltip(
                  <button type="button" onClick={handleAccountSwitch} aria-label={`${tAuth('switchAccount')}: ${name}`}
                    className={cn(sidebarMenuButtonVariants({ size: 'icon' }))}>
                    <Avatar className="h-6 w-6 rounded-full">
                      <AvatarImage src={account.photoURL || undefined} alt="" referrerPolicy="no-referrer" />
                      <AvatarFallback name={name} className="rounded-full" />
                    </Avatar>
                  </button>,
                  `${tAuth('switchAccount')}: ${name}`,
                )}
              </SidebarMenuItem>
            );
          })}
          <SidebarMenuItem>
            {withTooltip(
              <button type="button" onClick={handleAccountSwitch} aria-label={tAuth('addAccount')} className={cn(sidebarMenuButtonVariants({ size: 'icon' }))}>
                <UserPlus aria-hidden />
              </button>,
              tAuth('addAccount'),
            )}
          </SidebarMenuItem>
        </>
      );
    }
    // One compact row that wraps when it must: label, the saved accounts as
    // small pills, Add account beside them (Gordon 2026-11-03).
    return (
      <li className={cn('list-none [direction:ltr]', isGridView && 'col-span-full')}>
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 menuright:flex-row-reverse">
          <span className="text-3xs font-medium uppercase tracking-wider text-muted-foreground">{tAuth('switchAccount')}</span>
          {otherSavedAccounts.map(account => {
            const name = account.displayName || account.email || '';
            return (
              <button
                key={account.uid}
                type="button"
                onClick={handleAccountSwitch}
                title={name}
                aria-label={`${tAuth('switchAccount')}: ${name}`}
                className="inline-flex h-9 min-w-0 items-center gap-2 rounded-full border border-sidebar-border px-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
              >
                <Avatar className="h-5 w-5 rounded-full">
                  <AvatarImage src={account.photoURL || undefined} alt="" referrerPolicy="no-referrer" />
                  <AvatarFallback name={name} className="rounded-full" />
                </Avatar>
                <span className="max-w-[10rem] truncate">{name}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleAccountSwitch}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-sidebar-border px-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
          >
            <UserPlus className="h-4 w-4" aria-hidden />
            {tAuth('addAccount')}
          </button>
        </div>
      </li>
    );
  };

  /** The account slot of the bottom row: Sign in (visitors) or the
   *  person's avatar + name + ▸, which opens the account menu in place. */
  const accountSlot = () => {
    if (loading) {
      return (
        <SidebarMenuItem>
          <SidebarMenuButton size={buttonSize} isActive={false} disabled tooltip={iconTip(t('loading'))}>
            <Loader2 className="animate-spin" />
            <span>{t('loading')}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
    if (!user) {
      return (
        // Visitors: a softly pulsing glow invites the next step (Gordon 2026-10-30).
        <SidebarMenuItem>
          <SidebarMenuButton href="/signin" size={buttonSize} isActive={pathname === "/signin"} className="bb-signup-glow" tooltip={iconTip(t('signIn'))}>
            <LogIn />
            <span>{t('signIn')}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
    const label = t('accountMenuFor', { name: accountName });
    const avatar = (
      <Avatar className={cn('shrink-0 rounded-full', isIconOnly || isGridView ? 'h-7 w-7' : 'h-6 w-6')}>
        <AvatarImage src={accountPhoto || undefined} alt="" referrerPolicy="no-referrer" />
        <AvatarFallback name={accountName} className="rounded-full" />
      </Avatar>
    );
    // A plain button (SidebarMenuButton closes the phone sheet / folds the
    // desktop menu on every click). Grid view: the tile spans two columns
    // and lays out like a list row.
    const size: ButtonSize = isIconOnly ? 'icon' : isMobile ? 'mobile' : 'default';
    const button = (
      <button
        ref={accountButtonRef}
        type="button"
        aria-expanded={accountOpen}
        aria-controls="bb-account-menu"
        aria-label={label}
        onClick={() => setAccountOpen(o => !o)}
        className={cn(
          sidebarMenuButtonVariants({ size }),
          isGridView && 'h-full justify-center gap-2 rounded-none px-2',
          accountOpen && 'bg-sidebar-accent text-sidebar-accent-foreground',
        )}
      >
        {avatar}
        {!isIconOnly && (
          <span className="flex min-w-0 items-center gap-1 font-semibold">
            {/* Grid tiles are narrow: the first name, as in the design. */}
            <span className="truncate">{isGridView ? accountName.split(/\s+/)[0] : accountName}</span>
            <ChevronRight
              aria-hidden
              className={cn('size-4 shrink-0 transition-transform duration-200 motion-reduce:transition-none', accountOpen && '-rotate-90')}
            />
          </span>
        )}
      </button>
    );
    return (
      <SidebarMenuItem className={cn(isGridView && 'col-span-2 aspect-auto')}>
        {withTooltip(button, label)}
      </SidebarMenuItem>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ── The menu itself scrolls; the bottom row below it never does ── */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <SidebarMenu ref={navContainerRef}>
        {/* ── Explore: the same for visitors and members, always on ── */}
        <GroupHeading label={t('explore')} iconOnly={isIconOnly} grid={isGridView} first />
        {menu.explore.map(item => (
          <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>
        ))}

        {/* ── My BB: + Create, then Dashboard + Profile, then the member's
            own pages in their own order (drag to move) ── */}
        {(menu.mybb.length > 0 || (user && menu.create.length > 0)) && <GroupHeading label={t('groupMyBb')} iconOnly={isIconOnly} grid={isGridView} />}
        {/* ── + Create first in My BB (Gordon 2026-11-03): one entry, its
            shortcuts open in place ── */}
        {user && menu.create.length > 0 && (
          <>
            <SidebarMenuItem>
              {helpEnabled ? (
                <HelpBubble title={t('create')} description={t('createHint')} side="right" block>
                  <CreateToggle open={createOpen} onToggle={toggleCreate} size={buttonSize} label={t('create')} controls="bb-menu-create" />
                </HelpBubble>
              ) : (
                <CreateToggle open={createOpen} onToggle={toggleCreate} size={buttonSize} label={t('create')} controls="bb-menu-create" />
              )}
            </SidebarMenuItem>
            <li id="bb-menu-create" hidden={!createOpen} className={cn('list-none', isGridView && 'col-span-full')}>
              <SidebarMenu
                aria-label={t('create')}
                className={cn(
                  !isIconOnly && !isGridView && 'p-0 pl-4 menuright:pl-0 menuright:pr-4',
                  isGridView && 'bg-sidebar-accent/40',
                  isIconOnly && 'bg-sidebar-accent/40',
                )}
              >
                {menu.create.map(item => (
                  <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>
                ))}
              </SidebarMenu>
            </li>
          </>
        )}

        {pinnedMyBb.map(item => (
          <SidebarMenuItem key={item.id}>{pageButton(item, { indent: canDrag })}</SidebarMenuItem>
        ))}
        {movableMyBb.map((item, index) => (
          <SidebarMenuItem
            key={item.id}
            data-nav-index={index}
            onPointerDown={user ? (e) => handlePointerDown(e, index) : undefined}
            onPointerMove={user ? (e) => handlePointerMove(e, index) : undefined}
            onPointerUp={user ? (e) => handlePointerUp(e, index) : undefined}
            onPointerCancel={() => { setDraggingIndex(null); setDragOverIndex(null); isDragActive.current = false; }}
            className={cn(
              "relative group/navitem transition-all select-none",
              draggingIndex === index && "opacity-40 scale-95",
              dragOverIndex === index && draggingIndex !== index && "border-t-2 border-primary",
              user && "cursor-grab active:cursor-grabbing"
            )}
          >
            {/* Grip hint — list view only */}
            {canDrag && (
              <span
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover/navitem:opacity-40 transition-opacity pl-0.5 cursor-grab menuright:left-auto menuright:right-0 menuright:pl-0 menuright:pr-0.5"
                aria-hidden="true"
              >
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            )}
            {pageButton(item, { indent: canDrag })}
          </SidebarMenuItem>
        ))}

        {/* ── More: off unless switched on in Settings → Layout ── */}
        {menu.more.length > 0 && <GroupHeading label={t('groupMore')} iconOnly={isIconOnly} grid={isGridView} />}
        {menu.more.map(item => (
          <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>
        ))}
        {/* ── End: BB story + Beta demo, last, for everyone ── */}
        {menu.end.length > 0 && <li aria-hidden="true" className={cn('mx-3 my-2 h-px list-none bg-sidebar-border/70', isGridView && 'col-span-full')} />}
        {menu.end.map(item => (
          <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>
        ))}
      </SidebarMenu>
      </div>

      {/* ── Visitors: Download App just above the bottom row (members find it
          in the account menu) ── */}
      {!loading && !user && (
        <SidebarMenu className="shrink-0">
          <PWAInstallNavItem buttonSize={buttonSize} />
        </SidebarMenu>
      )}

      {/* ── Bottom row: pinned to the bottom of the menu — exactly Themes,
          Help, Account, the same order and place signed in or not (Andrés's
          menu, 2026-11-03). The account menu opens in place above the
          avatar, so the avatar never moves. ── */}
      <div
        className="shrink-0 border-t border-sidebar-border/70"
        onKeyDown={(e) => { if (e.key === 'Escape' && accountOpen) { e.stopPropagation(); closeAccount(true); } }}
      >
        {user && !loading && (
          <div id="bb-account-menu" hidden={!accountOpen}>
            {accountOpen && (
              <SidebarMenu aria-label={t('account')} className="bg-sidebar-accent/40">
                {menu.account.map(item => (
                  <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>
                ))}
                {switchAccountRows()}
                <PWAInstallNavItem buttonSize={buttonSize} />
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={signOut} size={buttonSize} isActive={false} tooltip={iconTip(t('signOut'))}>
                    <LogOut />
                    <span>{t('signOut')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            )}
          </div>
        )}
        <SidebarMenu>
          {menu.bottom.map(item => {
            if (item.id === 'themes') return <ThemeSidebarItem key={item.id} buttonSize={buttonSize} label={t('themes')} />;
            if (item.id === 'account') return <Fragment key={item.id}>{accountSlot()}</Fragment>;
            return <SidebarMenuItem key={item.id}>{pageButton(item)}</SidebarMenuItem>;
          })}
        </SidebarMenu>
      </div>

      {/* Language switcher at the bottom of the nav — not on phones, where the
          menu's own header already has it (it showed twice). Desktop: a full
          cell sitting right on the bottom-left corner button, no gap (Gordon
          2026-10-31). */}
      {!isMobile && (
        // One corner-button wide, on the menu's outer edge — right above the
        // chat / player-toggle cell on either side (Gordon 2026-10-31).
        <div className="flex shrink-0 justify-start menuright:justify-end">
          <LanguageSwitcher variant="compact" stacked={buttonSize === 'icon'} className="h-[50px] w-[var(--sidebar-width-icon)] rounded-none px-0" />
        </div>
      )}
    </div>
  );
}
